import { useCallback, useEffect, useRef, useState } from "react";
import { publishCompactSearch } from "../utils/compactSearchBus";

// How much height collapsing .toolbar-search-row actually gives up (its own
// max-height:60px, plus .sticky-toolbar's padding-top/bottom going to 0 while
// collapsed) - with a buffer on top, since this only needs to be a safe
// upper bound, not exact.
const COLLAPSE_DELTA_PX = 120;

// Distance (px) from the viewport top at which the sentinel (placed right
// before .sticky-toolbar) starts counting as "the toolbar has reached its
// stuck position" - matches the old IntersectionObserver's rootMargin of
// "-71px 0px 0px 0px" (a 1px-tall target stops intersecting a viewport
// shrunk by 71px from the top once it's scrolled 71px above the real top).
const COLLAPSE_START_PX = 71;

// How many px of further scroll it takes to go from fully expanded to fully
// collapsed - matches .toolbar-search-row's own 60px max-height, so the row
// visually finishes shrinking exactly as this much extra scroll happens.
const COLLAPSE_RANGE_PX = 60;

// Telegram-style collapse: once the page's sticky search toolbar scrolls up
// under the fixed header, shrink it away and let Header.js show a compact
// search (and optional create) icon in its place instead. Tapping that icon
// jumps straight back to the page's own real search box (scroll + focus)
// rather than re-implementing search in the header.
//
// Driven directly by scroll position (a plain `scroll` listener + rAF),
// not a boolean IntersectionObserver flag flipped through a timed CSS
// transition - a fixed-duration transition can't track a continuous,
// input-driven scroll gesture 1:1, so at the moment it fires the content
// visibly jumps out of step with the finger/wheel that's still scrolling
// underneath it. `progress` (0 = expanded, 1 = collapsed) instead moves in
// exact lockstep with scroll, every frame, so callers can drive maxHeight/
// opacity/padding straight off it with no transition at all.
export default function useCompactToolbar({ onExpand, onCreate }) {
    const toolbarSentinelRef = useRef(null);
    // Starts collapsed (nothing in the content, only the header's own
    // icons) - not derived from scroll position, see activatedRef below.
    const [progress, setProgress] = useState(1);
    const progressRef = useRef(1);
    const rafRef = useRef(null);

    // Scroll position only gets to drive `progress` once the user has
    // actually tapped the header's search icon at least once this mount
    // (set in handleExpand below). Without this gate, a page loaded already
    // scrolled to the top would compute raw=0 and pop the search row (and
    // its Create button) open by itself - the whole point here is that
    // neither ever shows up in the content by default, on any scroll
    // position, until the user asks for it.
    const activatedRef = useRef(false);
    // Reactive mirror of activatedRef, for callers that need to tell "still
    // the untouched default state" apart from "opened once, then scrolled
    // back to collapsed" - both read as progress===1/isCompact, but only
    // the former should reserve the page-top breathing-room gap. See
    // toolbarStyle in the consuming pages.
    const [activated, setActivated] = useState(false);

    // The raw, scroll-position-derived value (ignores the "tap to expand"
    // override below) - used both to drive `progress` on every scroll frame
    // and to know when a forced expand has naturally caught back up with
    // real scroll position, so it can hand control back without a jump.
    const computeRawProgress = useCallback(() => {
        const el = toolbarSentinelRef.current;
        if (!el) return 0;
        const top = el.getBoundingClientRect().top;
        const naiveRaw = Math.min(1, Math.max(0, (COLLAPSE_START_PX - top) / COLLAPSE_RANGE_PX));

        // Guards against a short list (e.g. an admin with only a couple of
        // tournaments) collapsing/expanding forever: shrinking the search
        // row on scroll gives back COLLAPSE_DELTA_PX of page height - on a
        // list barely taller than the viewport, that's enough to make the
        // whole page fit again, so the browser snaps scrollTop back to 0,
        // which brings the sentinel back into view, re-expanding the row,
        // growing the page back past the viewport, and repeating - visible
        // as the list jumping up/down instead of settling. Only allow the
        // collapse at all once the page has real scroll headroom - measured
        // at its expanded (tallest) height - that still leaves it scrollable
        // after giving up COLLAPSE_DELTA_PX.
        //
        // Computed fresh off live geometry right here, on every scroll
        // frame, rather than a separately polled/cached flag - a cached
        // value is inherently stale for up to a poll interval, and a stale
        // "yes, safe to collapse" that flips a moment after the bar has
        // already started shrinking read as it snapping back open right
        // after - just as ugly on a short list as the oscillation itself.
        const expandedScrollHeight = document.documentElement.scrollHeight
            + naiveRaw * COLLAPSE_DELTA_PX;
        const scrollable = expandedScrollHeight - window.innerHeight;
        if (scrollable <= COLLAPSE_DELTA_PX) return 0;

        return naiveRaw;
    }, []);

    // handleExpand/publish both need the latest onExpand/onCreate without
    // taking a dependency on them - callers (TournamentsList etc.) pass
    // inline closures that get a new identity every render, and depending
    // on them directly would republish (and briefly null out, see the
    // effect below) the header's compact-search icon on every keystroke.
    const onExpandRef = useRef(onExpand);
    const onCreateRef = useRef(onCreate);
    useEffect(() => {
        onExpandRef.current = onExpand;
        onCreateRef.current = onCreate;
    });
    // onCreate flips null<->handler when the caller's own is_authorized
    // changes (e.g. TournamentsList staying mounted across the Перегляд/
    // Редагування switch, App.js's /tournaments+/admin/tournaments route
    // merge) - a primitive, so unlike the closure itself this is safe to
    // depend on directly without republishing on every keystroke.
    const hasCreate = !!onCreate;

    // Tapping the compact icon used to just scrollTo(0,0) and wait for the
    // observer to notice the sentinel is back in view before un-collapsing
    // the toolbar - that round trip (paint the scroll, THEN a beat later
    // animate the search row open) read as the list jumping right after it
    // had already settled. Setting progress to 0 directly, in the same tick
    // as the click (before scrollTo even runs), means both happen in the
    // same paint - the very next scroll event then confirms/keeps it at 0
    // once the jump lands, so there's nothing left to "release" later.
    const forcedRef = useRef(false);

    useEffect(() => {
        const applyProgress = () => {
            if (!activatedRef.current) return;
            const raw = computeRawProgress();
            if (forcedRef.current) {
                if (raw <= 0) forcedRef.current = false;
                else return; // still overriding - a real scroll event will confirm/clear it above
            }
            if (progressRef.current === raw) return;
            progressRef.current = raw;
            setProgress(raw);
        };

        const onScroll = () => {
            if (rafRef.current) return;
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = null;
                applyProgress();
            });
        };

        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isCompact = progress >= 1;

    // For callers to re-collapse the row themselves (e.g. on blur with
    // nothing entered) instead of only ever collapsing via scroll. Resets
    // activatedRef back to false too, same as before the row was ever
    // opened - without that, the very next incidental scroll/resize event
    // (e.g. the browser scrolling a newly-focused element into view when
    // focus moves away) recomputes raw progress off real scroll position,
    // which is still 0 (page never actually moved), and immediately snaps
    // it back open again since applyProgress had nothing gating it once
    // activated. Resetting activatedRef makes an empty-blur collapse behave
    // exactly like a fresh, never-opened mount again.
    // Declared above the publish effect below (not just before the return)
    // since that effect closes over it - referencing a `const` before its
    // declaration in the same render throws (temporal dead zone).
    const collapse = useCallback(() => {
        activatedRef.current = false;
        setActivated(false);
        forcedRef.current = false;
        progressRef.current = 1;
        setProgress(1);
    }, []);

    // Published on mount and again whenever `activated` flips, so the
    // header's icon can tell "search is currently open" from "closed" and
    // toggle instead of only ever expanding (isOpen mirrors `activated`,
    // the same flag this hook's own callers use for that below). Not gated
    // on isCompact/onExpand/onCreate identity - see the refs above for why.
    useEffect(() => {
        const handleExpand = () => {
            activatedRef.current = true;
            setActivated(true);
            // Only worth "forcing" (ignoring scroll until it catches up)
            // when onExpand's scrollTo(0,0) actually has somewhere to go -
            // the toolbar can now start collapsed even while already at the
            // very top of the page (raw already <= 0 here), where that
            // scrollTo is a no-op and fires no real `scroll` event - without
            // this check forcedRef would never see the raw<=0 it's waiting
            // for, and progress would stay glued open forever, ignoring
            // every scroll-down from then on.
            if (computeRawProgress() > 0) {
                forcedRef.current = true;
            }
            progressRef.current = 0;
            setProgress(0);
            onExpandRef.current?.();
        };
        publishCompactSearch({ onExpand: handleExpand, onCollapse: collapse, onCreate: onCreateRef.current, isOpen: activated });
        return () => publishCompactSearch(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activated, collapse, computeRawProgress, hasCreate]);

    return { toolbarSentinelRef, isCompact, progress, activated, collapse };
}
